import { Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../../../../ui-components/shared.module';
import { CameraIconComponent } from '../../../../../@icons/camera-icon.component';
import { CloseIconComponent } from '../../../../../@icons/close-icon.component';
import {
  validateWillyPhoto,
  WILLY_PHOTO_MAX_COUNT,
  WillyPhotoError,
} from '../willy-photo.util';

export interface ChatMessageSubmit {
  text: string;
  photos?: File[];
}

interface PhotoPreview {
  file: File;
  url: string;
}

@Component({
    selector: 'vw-ai-chatbot-input-message',
    imports: [
        FormsModule,
        SharedModule,
        TranslateModule,
        CameraIconComponent,
        CloseIconComponent
    ],
    templateUrl: './ai-chatbot-input-message.component.html',
    styleUrl: './ai-chatbot-input-message.component.scss'
})
export class AiChatbotInputMessageComponent {
  @Output() messageSubmitted = new EventEmitter<ChatMessageSubmit>();
  @Output() inputFocus = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();

  @ViewChild('photoInput') photoInput: ElementRef<HTMLInputElement>;

  message: string = '';
  photos: PhotoPreview[] = [];
  photoError: WillyPhotoError | null = null;

  resetMessage() {
    this.message = '';
    this.removeAllPhotos();
  }

  /** Pre-fill the input (e.g. "Diagnose this plant" entry point). */
  setMessage(text: string) {
    this.message = text;
  }

  sendMessage() {
    if (this.message.length > 0 || this.photos.length > 0) {
      this.messageSubmitted.emit({
        text: this.message,
        photos: this.photos.length ? this.photos.map((p) => p.file) : undefined,
      });
      // The File references stay valid for the upload; only local state is cleared
      this.message = '';
      this.photos.forEach((p) => URL.revokeObjectURL(p.url));
      this.photos = [];
      this.photoError = null;
    }
  }

  onEnter() {
    this.sendMessage();
  }

  onSendButtonClick() {
    this.sendMessage();
  }

  onPhotoButtonClick() {
    this.photoInput.nativeElement.click();
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    // Allow re-selecting the same file after a remove
    input.value = '';
    this.photoError = null;
    for (const file of files) {
      if (this.photos.length >= WILLY_PHOTO_MAX_COUNT) {
        this.photoError = 'too-many';
        break;
      }
      const error = validateWillyPhoto(file);
      if (error) {
        this.photoError = error;
        continue;
      }
      this.photos.push({ file, url: URL.createObjectURL(file) });
    }
  }

  removePhoto(index: number) {
    const [removed] = this.photos.splice(index, 1);
    if (removed) {
      URL.revokeObjectURL(removed.url);
    }
    this.photoError = null;
  }

  private removeAllPhotos() {
    this.photos.forEach((p) => URL.revokeObjectURL(p.url));
    this.photos = [];
    this.photoError = null;
  }

  ngOnDestroy() {
    this.photos.forEach((p) => URL.revokeObjectURL(p.url));
  }

  onInputFocus() {
    this.inputFocus.emit();
  }

  onInputBlur() {
    this.inputBlur.emit();
  }
}
